import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Coins, Zap, ShieldCheck, ArrowRight, CreditCard } from 'lucide-react';
import { useCurrency } from '../../contexts/CurrencyContext';

interface RechargeCardProps {
    onPurchase: (credits: number) => void;
    isLoading: boolean;
}

const RechargeCard: React.FC<RechargeCardProps> = ({ onPurchase, isLoading }) => {
    const [credits, setCredits] = useState<number>(500);
    const [inputValue, setInputValue] = useState<string>("500");
    const { currentSymbol, getPriceForCredits } = useCurrency();

    const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(e.target.value);
        setCredits(value);
        setInputValue(value.toString());
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setInputValue(val);
        const num = parseInt(val);
        if (!isNaN(num)) {
            setCredits(Math.min(10000, Math.max(0, num)));
        }
    };

    const totalPrice = getPriceForCredits(credits);

    return (
        <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-4xl bg-white rounded-[3rem] border border-gray-100 shadow-[0_10px_60px_-15px_rgba(124,58,237,0.12)] p-1.5 overflow-hidden"
        >
            <div className="flex flex-col lg:flex-row h-full">
                
                {/* Left Section: Selection Control */}
                <div className="flex-1 p-8 md:p-10 border-b lg:border-b-0 lg:border-r border-gray-100">
                    <div className="flex items-center gap-3 mb-8">
                        <div className="w-10 h-10 rounded-xl bg-violet-600 flex items-center justify-center shadow-lg shadow-violet-200">
                            <Coins size={20} className="text-white" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 tracking-tight font-outfit">Select Amount</h2>
                            <p className="text-gray-500 text-[11px] font-medium tracking-tight">Flexible recharge to power your growth</p>
                        </div>
                    </div>

                    <div className="space-y-12">
                        {/* Dynamic Slider Section */}
                        <div className="space-y-6">
                            <div className="flex justify-between items-end">
                                <label className="text-[11px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-1">Slide to fuel</label>
                                <div className="flex items-center gap-2 group">
                                    <div className="px-3 py-1 bg-gray-50 border border-gray-200 rounded-lg group-hover:border-violet-300 transition-colors">
                                        <input 
                                            type="text" 
                                            value={inputValue}
                                            onChange={handleInputChange}
                                            className="w-16 bg-transparent text-sm font-bold text-gray-900 focus:outline-none text-center tabular-nums"
                                        />
                                    </div>
                                    <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">Credits</span>
                                </div>
                            </div>
                            
                            <div className="relative h-4 flex items-center">
                                {/* Track Background */}
                                <div className="absolute inset-0 h-1.5 bg-gray-100 rounded-full my-auto" />
                                {/* Progressive Track */}
                                <div 
                                    className="absolute h-1.5 bg-gradient-to-r from-violet-600 to-fuchsia-500 rounded-full my-auto transition-all duration-300 ease-out"
                                    style={{ width: `${(credits / 5000) * 100}%` }}
                                />
                                {/* Custom Slider Input */}
                                <input 
                                    type="range" 
                                    min="10" 
                                    max="5000" 
                                    step="10"
                                    value={credits}
                                    onChange={handleSliderChange}
                                    className="w-full h-8 bg-transparent appearance-none cursor-pointer absolute z-20 focus:outline-none
                                    [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:h-6 
                                    [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-[5px] 
                                    [&::-webkit-slider-thumb]:border-violet-600 [&::-webkit-slider-thumb]:shadow-xl [&::-webkit-slider-thumb]:active:scale-125 
                                    [&::-webkit-slider-thumb]:transition-all"
                                />
                            </div>
                            
                            <div className="flex justify-between text-[10px] font-bold text-gray-800 tracking-[0.2em] uppercase">
                                <span>10 Credits</span>
                                <span className="text-violet-600/80">2500 Credits</span>
                                <span>5000 Credits</span>
                            </div>
                        </div>

                        {/* Summary Features */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="flex items-center gap-3 p-4 rounded-2xl bg-gray-50/50 border border-gray-100 group">
                                <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform">
                                    <Zap size={16} className="text-amber-500 fill-amber-500/10" />
                                </div>
                                <div className="space-y-0.5">
                                    <p className="text-[10px] font-bold text-gray-400 tracking-widest uppercase leading-none">Scrape Speed</p>
                                    <p className="text-xs font-bold text-gray-800">Instant Fuel</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 p-4 rounded-2xl bg-gray-50/50 border border-gray-100 group">
                                <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform">
                                    <CreditCard size={16} className="text-emerald-500" />
                                </div>
                                <div className="space-y-0.5">
                                    <p className="text-[10px] font-bold text-gray-400 tracking-widest uppercase leading-none">Payment</p>
                                    <p className="text-xs font-bold text-gray-800">Secure SSL</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Section: Visualized Value & Action */}
                <div className="w-full lg:w-[300px] bg-slate-950 p-10 flex flex-col justify-between relative overflow-hidden text-white rounded-b-[3rem] lg:rounded-b-none lg:rounded-r-[3rem]">
                    {/* Animated Glow in background */}
                    <div className="absolute top-0 right-0 w-64 h-64 bg-violet-600/20 blur-[100px] rounded-full" />
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-fuchsia-600/20 blur-[100px] rounded-full" />

                    <div className="relative z-10 space-y-5">
                        <div className="space-y-1.5">
                             <p className="text-[10px] font-bold text-slate-500 tracking-[0.3em] uppercase">Total Investment</p>
                             <div className="flex items-baseline gap-2">
                                <span className="text-3xl font-bold font-outfit tabular-nums tracking-tighter decoration-fuchsia-500 decoration-4 underline-offset-8">
                                    {currentSymbol}{totalPrice.toLocaleString()}
                                </span>
                             </div>
                        </div>
                        
                        <div className="space-y-3 pt-6">
                            <div className="flex items-center gap-3 text-xs font-semibold text-slate-300">
                                <ArrowRight size={12} className="text-violet-400" />
                                <span>{credits.toLocaleString()} Scrape Jobs</span>
                            </div>
                            <div className="flex items-center gap-3 text-xs font-semibold text-slate-300">
                                <ArrowRight size={12} className="text-violet-400" />
                                <span>Verified Email Leads</span>
                            </div>
                            <div className="flex items-center gap-3 text-xs font-semibold text-slate-300">
                                <ArrowRight size={12} className="text-violet-400" />
                                <span>Never Expires</span>
                            </div>
                        </div>
                    </div>

                    <div className="relative z-10 pt-8">
                        <button
                            onClick={() => onPurchase(credits)}
                            disabled={isLoading}
                            className="w-full h-14 bg-white hover:bg-slate-50 text-slate-950 rounded-[1rem] font-bold text-sm transition-all flex items-center justify-center gap-3 shadow-2xl active:scale-[0.98] group"
                        >
                            {isLoading ? (
                                <div className="w-5 h-5 border-3 border-slate-900/30 border-t-slate-900 rounded-full animate-spin" />
                            ) : (
                                <>
                                    Recharge Now <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                                </>
                            )}
                        </button>
                        <div className="mt-4 flex items-center justify-center gap-2 text-[9px] font-bold text-slate-500 tracking-widest uppercase">
                            <ShieldCheck size={11} className="text-emerald-500" />
                            Cryptomus Secure Checkout
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

export default RechargeCard;
